import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { insightsAPI } from '../../api';
import { MuscleMap } from '../../components/insights/MuscleMap';
import { CalendarHeatmap, ColorScale } from '../../components/insights/CalendarHeatmap';
import { Spinner } from '../../components/ui';
import { useCountUp, useInView, useReducedMotion } from '../../hooks/motion';
import {
  Activity, Flame, Target, ChevronRight, Dumbbell, Calendar,
  TrendingUp, RefreshCw, AlertCircle, Sparkles,
  Trophy, Heart, Brain, CheckCircle2, ArrowUpRight
} from 'lucide-react';

/**
 * InsightsPage — /insights
 *
 * Wow v2. The page now hosts three tabs (Coach · Muscles · Calendar)
 * and a page-level streak hero that loads first.
 *
 *   - Coach    → "The insight": synthesized narrative from both
 *                 muscle-engagement and calendar-heatmap endpoints.
 *   - Muscles  → "What you trained": body map, summary, recovery, ranking.
 *   - Calendar → "How consistent": heatmap, top months, coach line.
 *
 * Data is hoisted to the page level so the streak hero is always
 * populated and tab switches are instant. The legacy URL
 * /insights/muscles still resolves to the Muscles tab so old links
 * keep working.
 */
export const InsightsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // URL → tab. /insights/muscles legacy route maps to 'muscles';
  // plain /insights lands on the new 'coach' tab.
  const initialTab = (() => {
    if (typeof window === 'undefined') return 'coach';
    const p = new URLSearchParams(window.location.search);
    const fromQuery = p.get('tab');
    if (['coach', 'muscles', 'calendar'].includes(fromQuery)) return fromQuery;
    if (window.location.pathname.endsWith('/insights/muscles')) return 'muscles';
    return 'coach';
  })();
  const [tab, setTab] = useState(initialTab);

  // Hoisted state: both endpoints live at the page level so the
  // streak hero, the coach tab, and the calendar tab share one
  // fetch round-trip.
  const [muscles, setMuscles] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [muscleDays, setMuscleDays] = useState(7);
  const [calendarDays, setCalendarDays] = useState(365);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [muscleRefreshing, setMuscleRefreshing] = useState(false);
  const [calendarRefreshing, setCalendarRefreshing] = useState(false);

  const loadMuscles = useCallback(async () => {
    setMuscleRefreshing(true);
    try {
      const res = await insightsAPI.getMuscleEngagement(muscleDays);
      setMuscles(res.data);
    } catch (err) {
      console.error('Failed to load muscle engagement:', err);
      setPageError(t('musclesPage.error', { defaultValue: 'Could not load muscle engagement.' }));
    } finally {
      setMuscleRefreshing(false);
    }
  }, [muscleDays, t]);

  const loadCalendar = useCallback(async () => {
    setCalendarRefreshing(true);
    try {
      const res = await insightsAPI.getCalendarHeatmap(calendarDays);
      setCalendar(res.data);
    } catch (err) {
      console.error('Failed to load calendar heatmap:', err);
      setPageError(t('calendarTab.error', { defaultValue: 'Could not load calendar.' }));
    } finally {
      setCalendarRefreshing(false);
    }
  }, [calendarDays, t]);

  // Initial load: both endpoints in parallel.
  useEffect(() => {
    setPageLoading(true);
    setPageError('');
    Promise.all([
      insightsAPI.getMuscleEngagement(muscleDays).then((r) => setMuscles(r.data)).catch((e) => {
        console.error('Initial muscle load failed:', e);
      }),
      insightsAPI.getCalendarHeatmap(calendarDays).then((r) => setCalendar(r.data)).catch((e) => {
        console.error('Initial calendar load failed:', e);
      }),
    ]).finally(() => setPageLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Period changes refetch the relevant endpoint.
  useEffect(() => { loadMuscles(); }, [loadMuscles]);
  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  if (pageError && !calendar && !muscles) {
    return <ErrorState message={pageError} onRetry={() => { setPageError(''); setPageLoading(true); Promise.all([insightsAPI.getMuscleEngagement(muscleDays), insightsAPI.getCalendarHeatmap(calendarDays)]).then(([m, c]) => { setMuscles(m.data); setCalendar(c.data); }).catch((e) => setPageError(t('common.error', { defaultValue: 'Could not load insights.' }))).finally(() => setPageLoading(false)); }} t={t} />;
  }

  if (pageLoading && !calendar) {
    return (
      <div className="flex items-center justify-center min-h-[420px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 relative" data-testid="insights-page">
      <PageHeader t={t} />
      <StreakHero summary={calendar?.summary} t={t} />
      <TabBar tab={tab} setTab={setTab} t={t} />
      <div className="tab-panel-enter" key={tab}>
        {tab === 'coach' && (
          <CoachTab
            muscles={muscles}
            calendar={calendar}
            t={t}
          />
        )}
        {tab === 'muscles' && (
          <MusclesTab
            data={muscles}
            days={muscleDays}
            setDays={setMuscleDays}
            onRefresh={loadMuscles}
            refreshing={muscleRefreshing}
            navigate={navigate}
            t={t}
          />
        )}
        {tab === 'calendar' && (
          <CalendarTab
            data={calendar}
            days={calendarDays}
            setDays={setCalendarDays}
            onRefresh={loadCalendar}
            refreshing={calendarRefreshing}
            t={t}
          />
        )}
      </div>
    </div>
  );
};

// ===========================================================================
// Page chrome
// ===========================================================================

function PageHeader({ t }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div>
        <span className="hero-kicker">
          {t('insightsPage.kicker', { defaultValue: 'Insights' })}
        </span>
        <h1 className="mt-2 font-display text-3xl uppercase text-[var(--color-text)]">
          {t('insightsPage.title', { defaultValue: 'Your training, visualised' })}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-muted)] max-w-2xl">
          {t('insightsPage.subtitle', {
            defaultValue:
              'A body map of what you trained, and a calendar of how often. Together they tell the story of your consistency.',
          })}
        </p>
      </div>
    </div>
  );
}

function TabBar({ tab, setTab, t }) {
  const tabs = [
    { key: 'coach', label: t('insightsPage.tabCoach', { defaultValue: 'Coach' }), icon: Sparkles },
    { key: 'muscles', label: t('insightsPage.tabMuscles', { defaultValue: 'Muscles' }), icon: Flame },
    { key: 'calendar', label: t('insightsPage.tabCalendar', { defaultValue: 'Calendar' }), icon: Calendar },
  ];
  return (
    <div
      className="inline-flex p-1 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] backdrop-blur-md"
      role="tablist"
    >
      {tabs.map(({ key, label, icon: Icon }) => {
        const active = tab === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${
              active
                ? 'bg-primary-300 text-[#0a1208] tab-active-glow'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.05]'
            }`}
            data-testid={`insights-tab-${key}`}
            role="tab"
            aria-selected={active}
          >
            <Icon className={`w-4 h-4 ${active ? '' : 'opacity-70'}`} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ===========================================================================
// Muscles tab
// ===========================================================================

function MusclesTab({ data, days, setDays, onRefresh, refreshing, navigate, t }) {
  const [selectedMuscle, setSelectedMuscle] = useState(null);

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-[420px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <MusclesHeader
        days={days}
        setDays={setDays}
        onRefresh={onRefresh}
        refreshing={refreshing}
        t={t}
      />
      <SummaryRow data={data} t={t} />
      {data.summary.empty_state ? (
        <EmptyMusclesState t={t} onLogWorkout={() => navigate('/workout-log')} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-4">
          <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] overflow-hidden card-lift">
            <MuscleMap
              engagement={data.groups}
              onMuscleClick={(key, stats) => setSelectedMuscle({ key, stats })}
              height={460}
            />
          </div>
          <RecoveryPanel groups={data.groups} t={t} />
        </div>
      )}
      {selectedMuscle && (
        <MuscleDetailPanel
          muscle={selectedMuscle.key}
          stats={selectedMuscle.stats}
          onClose={() => setSelectedMuscle(null)}
          t={t}
        />
      )}
      <MusclesRankedList groups={data.groups} onSelect={(k, s) => setSelectedMuscle({ key: k, stats: s })} t={t} />
    </>
  );
}

function MusclesHeader({ days, setDays, onRefresh, refreshing, t }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div>
        <span className="hero-kicker">{t('insightsPage.muscles.kicker', { defaultValue: 'Body map' })}</span>
        <h2 className="mt-1 font-display text-xl uppercase text-[var(--color-text)]">
          {t('insightsPage.muscles.title', { defaultValue: 'What you trained' })}
        </h2>
      </div>
      <div className="flex items-center gap-3">
        <PeriodSelector value={days} onChange={setDays} options={[7, 30, 90]} t={t} />
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm font-medium hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          data-testid="muscles-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
  );
}

function SummaryRow({ data, t }) {
  const { summary, period_days } = data;
  const cards = [
    {
      label: t('insightsPage.summary.volume', { defaultValue: 'Total volume' }),
      value: summary.total_volume ? Math.round(summary.total_volume).toLocaleString() : '0',
      unit: t('insightsPage.summary.kgLbs', { defaultValue: 'kg·reps' }),
      icon: Dumbbell,
      color: 'primary',
    },
    {
      label: t('insightsPage.summary.musclesTrained', { defaultValue: 'Muscles trained' }),
      value: `${summary.muscles_trained}/6`,
      unit: t('insightsPage.summary.thisPeriod', { defaultValue: `last ${period_days} d` }),
      icon: Target,
      color: 'blue',
    },
    {
      label: t('insightsPage.summary.logs', { defaultValue: 'Workouts logged' }),
      value: summary.logs_in_period,
      unit: t('insightsPage.summary.inPeriod', { defaultValue: 'in period' }),
      icon: Activity,
      color: 'purple',
    },
    {
      label: t('insightsPage.summary.bestMuscle', { defaultValue: 'Most trained' }),
      value: summary.most_trained
        ? t(`muscleMap.muscle.${summary.most_trained}`, { defaultValue: summary.most_trained })
        : '—',
      unit: summary.most_skipped
        ? `${t('muscleMap.muscle.' + summary.most_skipped, { defaultValue: summary.most_skipped })} skipped`
        : t('insightsPage.summary.everything', { defaultValue: 'everything hit' }),
      icon: Flame,
      color: 'amber',
    },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="muscles-summary">
      {cards.map((c) => (
        <SummaryCard key={c.label} {...c} />
      ))}
    </div>
  );
}

function SummaryCard({ label, value, unit, icon: Icon, color = 'primary' }) {
  const colorClasses = {
    primary: 'border-[rgba(213,255,123,0.18)] bg-[rgba(213,255,123,0.05)] text-primary-100',
    blue: 'border-[rgba(95,180,255,0.18)] bg-[rgba(95,180,255,0.05)] text-blue-200',
    purple: 'border-[rgba(195,140,255,0.18)] bg-[rgba(195,140,255,0.05)] text-purple-200',
    amber: 'border-[rgba(255,210,125,0.18)] bg-[rgba(255,210,125,0.05)] text-amber-200',
  };
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className={cn('p-2 rounded-xl border shrink-0', colorClasses[color])}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-right">
          <div className="font-display text-2xl text-[var(--color-text)] leading-none">{value}</div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            {unit}
          </div>
        </div>
      </div>
      <div className="mt-3 text-xs text-[var(--color-text-muted)]">{label}</div>
    </div>
  );
}

function EmptyMusclesState({ t, onLogWorkout }) {
  return (
    <div
      className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[rgba(8,13,11,0.4)] p-12 text-center"
      data-testid="muscles-empty"
    >
      <div className="mx-auto h-14 w-14 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] flex items-center justify-center mb-4">
        <Dumbbell className="w-7 h-7 text-primary-100" />
      </div>
      <p className="font-display text-xl text-[var(--color-text)]">
        {t('insightsPage.muscles.emptyTitle', { defaultValue: 'No workouts logged yet' })}
      </p>
      <p className="mt-1 text-sm text-[var(--color-text-muted)] max-w-md mx-auto">
        {t('insightsPage.muscles.emptyHint', {
          defaultValue:
            'Log a workout to see your body map light up. Each exercise contributes to the muscles it trains.',
        })}
      </p>
      <button
        type="button"
        onClick={onLogWorkout}
        className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-300 text-[#0a1208] text-sm font-semibold hover:from-primary-300 hover:to-primary-200 transition-all"
        data-testid="muscles-empty-log"
      >
        {t('insightsPage.muscles.emptyCta', { defaultValue: 'Log a workout' })}
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function MuscleDetailPanel({ muscle, stats, onClose, t }) {
  const label = t(`muscleMap.muscle.${muscle}`, { defaultValue: muscle });
  return (
    <div
      className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6"
      data-testid="muscle-detail"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="hero-kicker">{t('insightsPage.detail.kicker', { defaultValue: 'Muscle breakdown' })}</span>
          <h3 className="mt-1 font-display text-2xl text-[var(--color-text)] uppercase">{label}</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          {t('insightsPage.detail.close', { defaultValue: 'Close' })}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <SmallStat label="Sets" value={stats?.sets ?? 0} />
        <SmallStat label="Volume" value={Math.round(stats?.volume ?? 0).toLocaleString()} />
        <SmallStat label="Intensity" value={`${Math.round((stats?.intensity ?? 0) * 100)}%`} />
      </div>
      {stats?.exercises?.length > 0 ? (
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[var(--color-text-muted)] mb-2">
            {t('insightsPage.detail.exercises', { defaultValue: 'Exercises that hit it' })}
          </p>
          <ul className="space-y-1">
            {stats.exercises.map((ex) => (
              <li
                key={ex}
                className="flex items-center gap-2 text-sm text-[var(--color-text)]"
                data-testid={`muscle-detail-ex-${ex}`}
              >
                <ChevronRight className="w-3 h-3 text-primary-200" />
                {ex}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-[var(--color-text-muted)]">
          {t('insightsPage.detail.noExercises', { defaultValue: 'No specific exercises logged for this muscle yet.' })}
        </p>
      )}
    </div>
  );
}

function MusclesRankedList({ groups, onSelect, t }) {
  const sorted = [...groups].sort((a, b) => b.volume - a.volume);
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="hero-kicker">{t('insightsPage.ranked.kicker', { defaultValue: 'Volume ranking' })}</span>
          <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
            {t('insightsPage.ranked.title', { defaultValue: 'By total volume' })}
          </h3>
        </div>
        <TrendingUp className="w-5 h-5 text-primary-200" />
      </div>
      <ul className="space-y-2" data-testid="muscles-ranked">
        {sorted.map((g, idx) => {
          const label = t(`muscleMap.muscle.${g.key}`, { defaultValue: g.key });
          return (
            <li key={g.key}>
              <button
                type="button"
                onClick={() => onSelect(g.key, g)}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-2xl border border-[var(--color-border)] bg-white/[0.02] hover:bg-white/[0.05] transition-colors"
                data-testid={`muscles-ranked-row-${g.key}`}
              >
                <div
                  className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-display ${
                    idx === 0
                      ? 'bg-gradient-to-br from-primary-500 to-primary-300 text-[#0a1208]'
                      : 'bg-white/[0.04] text-[var(--color-text-muted)] border border-[var(--color-border)]'
                  }`}
                >
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[var(--color-text)] font-medium">{label}</span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                      {g.sets} sets
                    </span>
                  </div>
                  <VolumeBar volume={g.volume} max={sorted[0]?.volume || 1} />
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-sm text-[var(--color-text)]">
                    {Math.round(g.volume).toLocaleString()}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                    volume
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function VolumeBar({ volume, max }) {
  const pct = max > 0 ? Math.min((volume / max) * 100, 100) : 0;
  return (
    <div className="mt-1.5 h-1.5 rounded-full bg-white/[0.04] border border-[var(--color-border)] overflow-hidden">
      <div
        className="h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-300"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function SmallStat({ label, value }) {
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-white/[0.04] p-3 text-center">
      <div className="font-display text-xl text-[var(--color-text)]">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] mt-1">
        {label}
      </div>
    </div>
  );
}

// ===========================================================================
// Calendar tab
// ===========================================================================

function CalendarTab({ data, days, setDays, onRefresh, refreshing, t }) {
  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-[420px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <div
        className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6 card-lift"
        data-testid="calendar-heatmap-card"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <span className="hero-kicker">{t('calendarTab.kicker', { defaultValue: 'Consistency' })}</span>
            <h2 className="mt-1 font-display text-xl uppercase text-[var(--color-text)]">
              {t('calendarTab.title', { defaultValue: 'Workout volume' })}
            </h2>
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">
              {t('calendarTab.subtitle', {
                defaultValue: 'Each square is a day. Darker = more volume. Click to pin a day.',
              })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <PeriodSelector value={days} onChange={setDays} options={[90, 180, 365, 730]} t={t} />
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm font-medium hover:bg-white/[0.08] transition-colors disabled:opacity-50"
              data-testid="calendar-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {data.summary.empty_state ? (
          <EmptyCalendarState t={t} />
        ) : (
          <>
            <CalendarHeatmap days={data.days} />
            <div className="mt-4 flex items-center justify-between">
              <ColorScale t={t} />
              <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                {data.start_date} → {data.end_date}
              </p>
            </div>
          </>
        )}
      </div>

      <CalendarSummary data={data} t={t} />
    </>
  );
}

function StreakHero({ summary, t }) {
  const [ref, inView] = useInView({ threshold: 0.2 });
  const reduced = useReducedMotion();
  const safeSummary = useMemo(
    () => summary || { current_streak: 0, longest_streak: 0, total_active_days: 0, total_volume: 0 },
    [summary]
  );
  const hasStreak = safeSummary.current_streak > 0;

  const streakN = useCountUp(safeSummary.current_streak, { start: inView && !reduced, duration: 1500 });
  const longestN = useCountUp(safeSummary.longest_streak, { start: inView && !reduced, duration: 1700 });
  const activeN = useCountUp(safeSummary.total_active_days, { start: inView && !reduced, duration: 1500 });

  // Coach line — generated locally from real numbers. This is the
  // "insight" part of the hero: a one-line read on the data.
  const coachLine = useMemo(() => makeStreakCoachLine(safeSummary, t), [safeSummary, t]);

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden rounded-[1.6rem] border p-6 md:p-8 ${
        hasStreak
          ? 'border-[rgba(213,255,123,0.32)] bg-[rgba(8,13,11,0.6)]'
          : 'border-[var(--color-border)] bg-[rgba(8,13,11,0.55)]'
      }`}
      data-testid="streak-hero"
    >
      {/* Ambient aurora — three blurred blobs that drift. */}
      <div className="ambient-aurora" aria-hidden="true">
        <div
          className="ambient-aurora__blob ambient-aurora__blob--lime"
          style={{ top: '-15%', left: '-10%', animation: 'auroraDrift 18s ease-in-out infinite' }}
        />
        <div
          className="ambient-aurora__blob ambient-aurora__blob--warm"
          style={{ top: '20%', right: '-12%', animation: 'auroraDrift 22s ease-in-out infinite reverse' }}
        />
        <div
          className="ambient-aurora__blob ambient-aurora__blob--cool"
          style={{ bottom: '-20%', left: '30%', animation: 'auroraDrift 26s ease-in-out infinite 2s' }}
        />
      </div>

      <div className="relative flex items-start gap-6 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <span className="hero-kicker inline-flex items-center gap-2">
            {hasStreak && <span className="dot-pulse" aria-hidden="true" />}
            {hasStreak
              ? t('calendarTab.streakActive', { defaultValue: 'Streak active' })
              : t('calendarTab.noStreak', { defaultValue: 'No active streak' })}
          </span>
          <div className="mt-3 flex items-baseline gap-3">
            <span
              className={`number-display text-[clamp(4rem,9vw,7rem)] ${hasStreak ? 'text-gradient-lime' : 'text-[var(--color-text)]'}`}
              style={hasStreak ? { animation: 'breath 3.4s ease-in-out infinite' } : undefined}
              data-testid="streak-hero-number"
            >
              {streakN}
            </span>
            <span className="text-sm text-[var(--color-text-muted)] uppercase tracking-[0.2em]">
              {safeSummary.current_streak === 1
                ? t('calendarTab.day', { defaultValue: 'day' })
                : t('calendarTab.days', { defaultValue: 'days' })}
            </span>
          </div>
          <p className="mt-3 text-base text-[var(--color-text)]/90 max-w-md leading-relaxed">
            {coachLine}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3 flex-1 min-w-[280px]">
          <HeroStat
            label={t('calendarTab.longestStreak', { defaultValue: 'Longest' })}
            value={longestN}
            unit={t('calendarTab.days', { defaultValue: 'days' })}
            icon={TrendingUp}
            color="primary"
            pulsing={hasStreak}
          />
          <HeroStat
            label={t('calendarTab.activeDays', { defaultValue: 'Active days' })}
            value={activeN}
            unit={t('calendarTab.inPeriod', { defaultValue: 'in period' })}
            icon={Activity}
            color="blue"
          />
          <HeroStat
            label={t('calendarTab.totalVolume', { defaultValue: 'Total volume' })}
            value={safeSummary.total_volume ? Math.round(safeSummary.total_volume).toLocaleString() : '0'}
            unit={t('calendarTab.units', { defaultValue: 'kg·reps' })}
            icon={Dumbbell}
            color="amber"
          />
        </div>
      </div>

      {hasStreak && (
        <div
          className="absolute top-4 right-4 text-primary-200 opacity-30 pointer-events-none breath-soft"
          aria-hidden="true"
        >
          <Flame className="w-28 h-28" strokeWidth={0.7} />
        </div>
      )}
    </div>
  );
}

function HeroStat({ label, value, unit, icon: Icon, color = 'primary', pulsing = false }) {
  const colorClasses = {
    primary: 'border-[rgba(213,255,123,0.18)] bg-[rgba(213,255,123,0.05)] text-primary-100',
    blue: 'border-[rgba(95,180,255,0.18)] bg-[rgba(95,180,255,0.05)] text-blue-200',
    amber: 'border-[rgba(255,210,125,0.18)] bg-[rgba(255,210,125,0.05)] text-amber-200',
  };
  return (
    <div className={`rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-3 ${pulsing ? 'glow-pulse-soft' : ''}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={cn('p-1.5 rounded-lg border', colorClasses[color])}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
          {label}
        </div>
      </div>
      <div className="font-display text-2xl text-[var(--color-text)] leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] mt-1">
        {unit}
      </div>
    </div>
  );
}

function EmptyCalendarState({ t }) {
  return (
    <div
      className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[rgba(8,13,11,0.3)] p-8 text-center"
      data-testid="calendar-empty"
    >
      <Calendar className="w-8 h-8 text-[var(--color-text-muted)] mx-auto mb-3" />
      <p className="font-display text-lg text-[var(--color-text)]">
        {t('calendarTab.emptyTitle', { defaultValue: 'No workouts to plot yet' })}
      </p>
      <p className="mt-1 text-sm text-[var(--color-text-muted)] max-w-md mx-auto">
        {t('calendarTab.emptyHint', {
          defaultValue: 'Once you start logging, this grid fills up. The streak begins on day one.',
        })}
      </p>
    </div>
  );
}

function CalendarSummary({ data, t }) {
  // Group active days by month and show top 3 months
  const monthly = {};
  for (const d of data.days) {
    if (d.workouts === 0) continue;
    const month = d.date.slice(0, 7);
    monthly[month] = (monthly[month] || 0) + 1;
  }
  const topMonths = Object.entries(monthly)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  if (topMonths.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6">
        <span className="hero-kicker">{t('calendarTab.bestMonths', { defaultValue: 'Top months' })}</span>
        <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
          {t('calendarTab.bestMonthsTitle', { defaultValue: 'Most active months' })}
        </h3>
        <ul className="mt-4 space-y-2">
          {topMonths.map(([month, count], idx) => {
            const max = topMonths[0][1];
            return (
              <li key={month} className="flex items-center gap-3">
                <div
                  className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-display ${
                    idx === 0
                      ? 'bg-gradient-to-br from-primary-500 to-primary-300 text-[#0a1208]'
                      : 'bg-white/[0.04] text-[var(--color-text-muted)] border border-[var(--color-border)]'
                  }`}
                >
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--color-text)]">
                    {formatMonth(month, t)}
                  </div>
                  <div className="h-1.5 rounded-full bg-white/[0.04] border border-[var(--color-border)] overflow-hidden mt-1">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-300"
                      style={{ width: `${(count / max) * 100}%` }}
                    />
                  </div>
                </div>
                <div className="font-display text-sm text-[var(--color-text)] shrink-0">
                  {count} {count === 1 ? t('calendarTab.day', { defaultValue: 'day' }) : t('calendarTab.days', { defaultValue: 'days' })}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <InsightCard data={data} t={t} />
    </div>
  );
}

function InsightCard({ data, t }) {
  const { summary } = data;
  const days = summary.total_active_days;
  const period = data.period_days;
  const pct = period > 0 ? Math.round((days / period) * 100) : 0;
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6">
      <span className="hero-kicker">{t('calendarTab.consistency', { defaultValue: 'Consistency' })}</span>
      <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
        {t('calendarTab.consistencyTitle', { defaultValue: 'How often you show up' })}
      </h3>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-display text-5xl text-[var(--color-text)]">{pct}%</span>
        <span className="text-sm text-[var(--color-text-muted)] uppercase tracking-[0.18em]">
          {t('calendarTab.ofDays', { defaultValue: `of last ${period} d` })}
        </span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-white/[0.04] border border-[var(--color-border)] overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-300 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-3 text-sm text-[var(--color-text-muted)]">
        {pct >= 70
          ? t('calendarTab.consistencyGreat', { defaultValue: 'Elite consistency. The body follows the calendar.' })
          : pct >= 40
          ? t('calendarTab.consistencyOk', { defaultValue: 'Solid week-on-week. A small bump builds the streak.' })
          : t('calendarTab.consistencyLow', { defaultValue: 'Lots of room to grow. Start with one session this week.' })}
      </p>
    </div>
  );
}

function formatMonth(yyyymm, t) {
  const [y, m] = yyyymm.split('-');
  const monthName = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
  return monthName;
}

// ===========================================================================
// Shared bits
// ===========================================================================

function PeriodSelector({ value, onChange, options, t }) {
  const labels = {
    7: '7 d',
    30: '30 d',
    90: '90 d',
    180: '6 m',
    365: '1 y',
    730: '2 y',
  };
  return (
    <div
      className="inline-flex p-1 rounded-2xl border border-[var(--color-border)] bg-white/[0.04]"
      role="tablist"
    >
      {options.map((o) => {
        const active = value === o;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
              active
                ? 'bg-primary-300 text-[#0a1208]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
            data-testid={`insights-period-${o}`}
          >
            {labels[o] || `${o} d`}
          </button>
        );
      })}
    </div>
  );
}

function ErrorState({ message, onRetry, t }) {
  return (
    <div className="rounded-2xl border border-red-500/30 bg-red-500/10 text-red-200 p-4 flex items-center gap-3">
      <AlertCircle className="w-5 h-5 shrink-0" />
      <p className="text-sm flex-1">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-xs font-semibold hover:bg-white/[0.08] transition-colors"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        {t('insightsPage.retry', { defaultValue: 'Retry' })}
      </button>
    </div>
  );
}

// ===========================================================================
// RecoveryPanel — sidebar that tells the user which muscles are
// ready to train, which are recovering, and which are at rest.
// Computed locally from each muscle's last_trained_at in the
// engagement data (the backend already ships this on every group).
// ===========================================================================
function RecoveryPanel({ groups, t }) {
  const [ref] = useInView({ threshold: 0.15 });
  const rows = useMemo(() => buildRecoveryRows(groups, t), [groups, t]);
  if (!groups || groups.length === 0) return null;
  return (
    <div
      ref={ref}
      className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.55)] p-5 flex flex-col card-lift"
      data-testid="recovery-panel"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="hero-kicker">
          {t('insightsPage.recovery.kicker', { defaultValue: 'Recovery' })}
        </span>
        <Heart className="w-4 h-4 text-primary-200" />
      </div>
      <h3 className="font-display text-lg uppercase text-[var(--color-text)]">
        {t('insightsPage.recovery.title', { defaultValue: "Who's ready" })}
      </h3>
      <p className="text-xs text-[var(--color-text-muted)] mb-4">
        {t('insightsPage.recovery.subtitle', { defaultValue: 'Days since you last hit each muscle.' })}
      </p>
      <ul className="space-y-2 flex-1">
        {rows.map((r, i) => (
          <li
            key={r.key}
            className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl border border-[var(--color-border)] bg-white/[0.02] tab-panel-enter"
            style={{ animationDelay: `${i * 80}ms` }}
            data-testid={`recovery-row-${r.key}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-sm text-[var(--color-text)] truncate">{r.label}</span>
              <span className={`recovery-chip recovery-chip--${r.status}`}>{r.statusLabel}</span>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] shrink-0">
              {r.sinceLabel}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function buildRecoveryRows(groups, t) {
  if (!groups || groups.length === 0) return [];
  const now = Date.now();
  const out = groups.map((g) => {
    const label = t(`muscleMap.muscle.${g.key}`, { defaultValue: g.key });
    let daysSince = null;
    if (g.last_trained_at) {
      const ts = typeof g.last_trained_at === 'string'
        ? new Date(g.last_trained_at).getTime()
        : g.last_trained_at;
      if (!Number.isNaN(ts)) daysSince = Math.floor((now - ts) / 86400000);
    }
    let status = 'idle';
    let statusLabel = t('insightsPage.recovery.untrained', { defaultValue: 'Untrained' });
    let sinceLabel = '—';
    if (daysSince === null) {
      status = 'idle';
      sinceLabel = t('insightsPage.recovery.never', { defaultValue: 'never' });
    } else if (daysSince === 0) {
      status = 'rest';
      statusLabel = t('insightsPage.recovery.rest', { defaultValue: 'Rest' });
      sinceLabel = t('insightsPage.recovery.today', { defaultValue: 'today' });
    } else if (daysSince <= 2) {
      status = 'recovering';
      statusLabel = t('insightsPage.recovery.recovering', { defaultValue: 'Recovering' });
      sinceLabel = t('insightsPage.recovery.daysAgo', { defaultValue: '{{n}}d ago', n: daysSince });
    } else {
      status = 'ready';
      statusLabel = t('insightsPage.recovery.ready', { defaultValue: 'Ready' });
      sinceLabel = t('insightsPage.recovery.daysAgo', { defaultValue: '{{n}}d ago', n: daysSince });
    }
    return { key: g.key, label, status, statusLabel, sinceLabel, daysSince };
  });
  // Order: ready first (most actionable), then recovering, then rest, then untrained.
  const rank = { ready: 0, recovering: 1, rest: 2, idle: 3 };
  out.sort((a, b) => {
    const r = (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
    if (r !== 0) return r;
    return (a.daysSince ?? 999) - (b.daysSince ?? 999);
  });
  return out;
}

// ===========================================================================
// CoachTab — synthesized insights from both endpoints. The "insight"
// part of the page; this is what should feel like a personal coach.
// All copy is data-driven. No AI, no API call — just honest reads on
// the real numbers, with motivational framing.
// ===========================================================================
function CoachTab({ muscles, calendar, t }) {
  const insights = useMemo(
    () => (muscles && calendar ? synthesizeInsights(muscles, calendar, t) : null),
    [muscles, calendar, t]
  );
  if (!muscles || !calendar) {
    return (
      <div className="flex items-center justify-center min-h-[420px]">
        <Spinner size="lg" />
      </div>
    );
  }
  if (calendar.summary.empty_state && muscles.summary.empty_state) {
    return <CoachEmptyState t={t} />;
  }
  if (!insights) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="coach-tab">
      <CoachHeadline insights={insights} t={t} />
      <CoachWorking insights={insights} t={t} />
      <CoachChange insights={insights} t={t} />
      <CoachGoal insights={insights} t={t} />
    </div>
  );
}

function CoachHeadline({ insights, t }) {
  return (
    <article
      className="coach-card coach-card--accent p-6 md:col-span-2 tab-panel-enter"
      data-testid="coach-headline"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-primary-200" />
        <span className="hero-kicker">
          {t('insightsPage.coach.headlineKicker', { defaultValue: 'Your month in one sentence' })}
        </span>
      </div>
      <h2
        className="font-display text-2xl md:text-3xl text-[var(--color-text)] uppercase leading-tight"
        dangerouslySetInnerHTML={{ __html: insights.headline }}
      />
      <hr className="rule-fade" />
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {insights.headlineStats.map((s, i) => (
          <div
            key={i}
            className="flex items-baseline gap-2 tab-panel-enter"
            style={{ animationDelay: `${200 + i * 80}ms` }}
          >
            <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">{s.label}</span>
            <span className="font-display text-lg text-[var(--color-text)]">{s.value}</span>
          </div>
        ))}
      </div>
    </article>
  );
}

function CoachWorking({ insights, t }) {
  return (
    <article
      className="coach-card p-6 tab-panel-enter"
      style={{ animationDelay: '180ms' }}
      data-testid="coach-working"
    >
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle2 className="w-4 h-4 text-primary-200" />
        <span className="hero-kicker">
          {t('insightsPage.coach.workingKicker', { defaultValue: "What's working" })}
        </span>
      </div>
      <ul className="space-y-3">
        {insights.working.map((w, i) => (
          <li
            key={i}
            className="flex items-start gap-3 tab-panel-enter"
            style={{ animationDelay: `${260 + i * 80}ms` }}
          >
            <span className="text-primary-200 mt-0.5 shrink-0">
              <ArrowUpRight className="w-4 h-4" />
            </span>
            <span
              className="text-sm text-[var(--color-text)]/90 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: w }}
            />
          </li>
        ))}
      </ul>
    </article>
  );
}

function CoachChange({ insights, t }) {
  return (
    <article
      className="coach-card coach-card--warm p-6 tab-panel-enter"
      style={{ animationDelay: '260ms' }}
      data-testid="coach-change"
    >
      <div className="flex items-center gap-2 mb-3">
        <Brain className="w-4 h-4 text-amber-200" />
        <span className="hero-kicker">
          {t('insightsPage.coach.changeKicker', { defaultValue: 'What to change' })}
        </span>
      </div>
      <ul className="space-y-3">
        {insights.change.map((c, i) => (
          <li
            key={i}
            className="flex items-start gap-3 tab-panel-enter"
            style={{ animationDelay: `${340 + i * 80}ms` }}
          >
            <span className="text-amber-200 mt-0.5 shrink-0">
              <ChevronRight className="w-4 h-4" />
            </span>
            <span
              className="text-sm text-[var(--color-text)]/90 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: c }}
            />
          </li>
        ))}
      </ul>
    </article>
  );
}

function CoachGoal({ insights, t }) {
  return (
    <article
      className="coach-card p-6 md:col-span-2 tab-panel-enter"
      style={{ animationDelay: '340ms' }}
      data-testid="coach-goal"
    >
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-primary-200" />
        <span className="hero-kicker">
          {t('insightsPage.coach.goalKicker', { defaultValue: 'One goal for this week' })}
        </span>
      </div>
      <p className="font-display text-xl md:text-2xl uppercase leading-tight text-gradient-lime">
        {insights.goal}
      </p>
    </article>
  );
}

function CoachEmptyState({ t }) {
  return (
    <div
      className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[rgba(8,13,11,0.4)] p-12 text-center"
      data-testid="coach-empty"
    >
      <div className="mx-auto h-14 w-14 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] flex items-center justify-center mb-4">
        <Sparkles className="w-7 h-7 text-primary-100" />
      </div>
      <p className="font-display text-xl text-[var(--color-text)]">
        {t('insightsPage.coach.emptyTitle', { defaultValue: 'No insights yet' })}
      </p>
      <p className="mt-1 text-sm text-[var(--color-text-muted)] max-w-md mx-auto">
        {t('insightsPage.coach.emptyHint', { defaultValue: 'Log a few workouts and the coach will start reading the patterns.' })}
      </p>
    </div>
  );
}

// ===========================================================================
// Streak coach line — generated from the calendar summary, used in
// the page-level hero.
// ===========================================================================
function makeStreakCoachLine(summary, t) {
  const cur = summary.current_streak || 0;
  const longest = summary.longest_streak || 0;
  const active = summary.total_active_days || 0;

  if (cur === 0 && active === 0) {
    return t('insightsPage.coach.firstLine', {
      defaultValue: 'A clean slate. Today is the first day of your story.',
    });
  }
  if (cur === 0) {
    return t('insightsPage.coach.restartLine', {
      defaultValue: 'Streak reset — but the data is still here. One workout restarts the engine.',
    });
  }
  if (cur >= longest && cur > 1) {
    return t('insightsPage.coach.recordLine', {
      defaultValue: "You're at your longest streak ever. The body is listening to the calendar.",
    });
  }
  return t('insightsPage.coach.normalLine', {
    defaultValue: 'Day {{n}} of a {{m}}-day personal best. Keep the engine warm.',
    n: cur,
    m: longest,
  });
}

// ===========================================================================
// synthesizeInsights — pure function, returns coach copy from real
// numbers. Used only by the Coach tab. Returns HTML-safe-ish strings
// with <span class="mark"> highlights for the headline / working /
// change sections; the goal is plain text.
// ===========================================================================
function synthesizeInsights(muscles, calendar, t) {
  const ms = muscles.summary || {};
  const cs = calendar.summary || {};
  const groups = muscles.groups || [];
  const days = calendar.days || [];
  const period = calendar.period_days || 365;

  const activeDays = cs.total_active_days || 0;
  const curStreak = cs.current_streak || 0;
  const topMuscle = ms.most_trained;
  const topMuscleLabel = topMuscle
    ? t(`muscleMap.muscle.${topMuscle}`, { defaultValue: topMuscle })
    : null;
  const totalVol = Math.round(ms.total_volume || 0);
  const logs = ms.logs_in_period || 0;

  // ---- Headline ----
  let headline;
  if (activeDays === 0) {
    headline = t('insightsPage.coach.headlineNone', {
      defaultValue: 'A blank canvas. The first workout is the most important one.',
    });
  } else if (logs === 0) {
    headline = t('insightsPage.coach.headlineNoLogs', {
      defaultValue: 'You opened the door. Now step through it.',
    });
  } else {
    const parts = [];
    parts.push(t('insightsPage.coach.headlineActive', {
      defaultValue: '<span class="mark">{{n}} active days</span>',
      n: activeDays,
    }));
    if (topMuscleLabel) {
      parts.push(t('insightsPage.coach.headlineTop', {
        defaultValue: 'top muscle <span class="mark">{{m}}</span>',
        m: topMuscleLabel,
      }));
    }
    if (curStreak > 0) {
      parts.push(t('insightsPage.coach.headlineStreak', {
        defaultValue: '<span class="mark">{{n}}-day streak</span>',
        n: curStreak,
      }));
    }
    headline = parts.join(' · ');
  }

  const headlineStats = [
    {
      label: t('insightsPage.coach.statVolume', { defaultValue: 'volume' }),
      value: totalVol.toLocaleString(),
    },
    {
      label: t('insightsPage.coach.statLogs', { defaultValue: 'workouts' }),
      value: logs,
    },
    {
      label: t('insightsPage.coach.statPeriod', { defaultValue: 'days' }),
      value: `${activeDays}/${period}`,
    },
  ];

  // ---- What's working ----
  const working = [];
  if (cs.longest_streak >= 7) {
    working.push(t('insightsPage.coach.workingStreak', {
      defaultValue: 'A <span class="mark">{{n}}-day personal best</span> — you proved you can come back.',
      n: cs.longest_streak,
    }));
  } else if (cs.longest_streak >= 3) {
    working.push(t('insightsPage.coach.workingStreak3', {
      defaultValue: '<span class="mark">{{n}} days in a row</span> is real momentum. Most members never get there.',
      n: cs.longest_streak,
    }));
  }
  if (topMuscleLabel && ms.muscles_trained >= 3) {
    working.push(t('insightsPage.coach.workingBalanced', {
      defaultValue: '<span class="mark">{{n}}/6</span> muscle groups hit. The body is getting a real signal, not a one-note story.',
      n: ms.muscles_trained,
    }));
  } else if (topMuscleLabel) {
    working.push(t('insightsPage.coach.workingTop', {
      defaultValue: "You're loyal to <span class=\"mark\">{{m}}</span>. Loyalty compounds.",
      m: topMuscleLabel,
    }));
  }
  if (totalVol > 0 && logs > 0) {
    const avg = Math.round(totalVol / logs);
    if (avg > 2000) {
      working.push(t('insightsPage.coach.workingHeavy', {
        defaultValue: "Average session: <span class=\"mark\">{{n}} vol</span>. You don't mess around.",
        n: avg.toLocaleString(),
      }));
    } else if (avg > 0) {
      working.push(t('insightsPage.coach.workingAvg', {
        defaultValue: 'Average session: <span class="mark">{{n}} vol</span>.',
        n: avg.toLocaleString(),
      }));
    }
  }
  if (working.length === 0) {
    working.push(t('insightsPage.coach.workingNone', {
      defaultValue: "You showed up. That's the only stat that matters on day one.",
    }));
  }

  // ---- What to change ----
  const change = [];
  if (ms.most_skipped) {
    const skipLabel = t(`muscleMap.muscle.${ms.most_skipped}`, {
      defaultValue: ms.most_skipped,
    });
    change.push(t('insightsPage.coach.changeSkipped', {
      defaultValue: '<span class="mark--warm">{{m}}</span> is being skipped. Even one light session unsticks the pattern.',
      m: skipLabel,
    }));
  }
  // Longest inactive gap (find the longest streak of days with zero workouts)
  if (days.length > 0) {
    let longestGap = 0;
    let currentGap = 0;
    for (const d of days) {
      if ((d.workouts || 0) > 0) {
        longestGap = Math.max(longestGap, currentGap);
        currentGap = 0;
      } else {
        currentGap += 1;
      }
    }
    longestGap = Math.max(longestGap, currentGap);
    if (longestGap >= 5) {
      change.push(t('insightsPage.coach.changeGap', {
        defaultValue: 'You disappeared for <span class="mark--warm">{{n}} days</span> at one point. One anchor workout per week prevents the next one.',
        n: longestGap,
      }));
    }
  }
  // Imbalance: top muscle > 2x second
  if (groups.length >= 2 && topMuscle && groups[0] && groups[1] &&
      groups[0].volume > 0 && groups[1].volume > 0 &&
      groups[0].volume / groups[1].volume > 2) {
    const secondLabel = t(`muscleMap.muscle.${groups[1].key}`, {
      defaultValue: groups[1].key,
    });
    change.push(t('insightsPage.coach.changeImbalance', {
      defaultValue: '<span class="mark--warm">{{m}}</span> is doing {{x}}× the volume of {{m2}}. The body likes balance.',
      m: topMuscleLabel,
      m2: secondLabel,
      x: Math.round(groups[0].volume / groups[1].volume),
    }));
  }
  if (change.length === 0) {
    change.push(t('insightsPage.coach.changeNone', {
      defaultValue: "No red flags. The pattern is healthy. Keep doing what you're doing.",
    }));
  }

  // ---- One goal ----
  let goal;
  if (ms.most_skipped) {
    const m = t(`muscleMap.muscle.${ms.most_skipped}`, { defaultValue: ms.most_skipped });
    goal = t('insightsPage.coach.goalSkip', {
      defaultValue: 'Hit {{m}} once this week — even bodyweight counts.',
      m,
    });
  } else if (curStreak === 0 && activeDays > 0) {
    goal = t('insightsPage.coach.goalRestart', {
      defaultValue: "One workout this week. That's all. The streak restarts itself.",
    });
  } else if (activeDays / Math.max(period, 1) < 0.3) {
    goal = t('insightsPage.coach.goalMore', {
      defaultValue: '3 sessions this week. Pick the days, block the calendar.',
    });
  } else {
    goal = t('insightsPage.coach.goalHold', {
      defaultValue: 'Hold the line. One session more than last week.',
    });
  }

  return { headline, headlineStats, working, change, goal };
}

function cn(...args) {
  return args.filter(Boolean).join(' ');
}

// Legacy export: MusclesPage so /insights/muscles deep-link still works.
// Renders the InsightsPage (which defaults to the Muscles tab).
export const MusclesPage = () => <InsightsPage />;
export default InsightsPage;
